import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  PackageCheck,
  RefreshCw,
  CornerDownLeft,
  ArrowUpDown,
  CalendarDays,
  ChevronDown,
  Check,
  Zap,
  Printer,
} from 'lucide-react';
import { OrderStatusTab } from '@/types/columns';
import useFilterOrderStore from '@/stores/useFilterOrderStore';
import { OrderTypeEnum } from '@/stores/useOrderTypeStore';
import { shortenNumber } from '@/utils';
import dayjs from 'dayjs';
import { ON_HOLD_FILTER_OPTIONS } from './ordersFilterOptions';

// ── Color tokens ─────────────────────────────────────────────────────────────

const C = {
  border: 'rgb(232, 230, 227)',
  mutedFg: 'rgb(115, 123, 140)',
  fg: 'rgb(57, 57, 60)',
  white: '#ffffff',
  primaryBg: 'rgba(237, 224, 74, 0.10)',
  primaryBorder: 'rgb(237, 224, 74)',
} as const;

// ── Status sub-options per tab ───────────────────────────────────────────────

// key = API sub-tab value (must match listStatusButtons.value)
// badgeColorKey = key into STATUS_HSL for the colored badge
const TAB_STATUS_OPTIONS: Record<string, { key: string; badgeColorKey: string; labelKey: string }[]> = {
  [OrderStatusTab.READY_TO_SHIP]: [
    { key: 'new', badgeColorKey: 'new', labelKey: 'new' },
    { key: 'scheduled', badgeColorKey: 'scheduled', labelKey: 'scheduled' },
  ],
  [OrderStatusTab.IN_PROGRESS]: [
    { key: 'collecting', badgeColorKey: 'collecting', labelKey: 'collecting' },
    { key: 'collected', badgeColorKey: 'collected', labelKey: 'collected' },
    { key: 'en-route', badgeColorKey: 'in_transit', labelKey: 'in_transit' },
    { key: 'out-for-delivery', badgeColorKey: 'out_for_delivery', labelKey: 'out_for_delivery' },
    { key: 'postponed', badgeColorKey: 'postponed', labelKey: 'postponed' },
  ],
  [OrderStatusTab.ON_HOLD]: ON_HOLD_FILTER_OPTIONS,
  [OrderStatusTab.RTO]: [
    { key: 'rto-requested', badgeColorKey: 'rto_requested', labelKey: 'rto_request' },
    { key: 'rto-scheduled', badgeColorKey: 'rto_scheduled', labelKey: 'return_scheduled' },
    { key: 'rto-received-warehouse', badgeColorKey: 'rto_received_warehouse', labelKey: 'returned_to_warehouse' },
    { key: 'rto-in-transit', badgeColorKey: 'rto_in_transit', labelKey: 'rto_in_transit' },
  ],
  [OrderStatusTab.FINISHED]: [
    { key: 'delivered', badgeColorKey: 'delivered', labelKey: 'delivered' },
    { key: 'rto-delivered', badgeColorKey: 'rto_delivered', labelKey: 'returned' },
    { key: 'rto-partially-delivered', badgeColorKey: 'rto_partially_delivered', labelKey: 'part_returned' },
    { key: 'partially-delivered', badgeColorKey: 'partially_delivered', labelKey: 'partially_delivered' },
    { key: 'claim-settled', badgeColorKey: 'claim_settled', labelKey: 'claim_settled' },
    { key: 'resend', badgeColorKey: 'resend', labelKey: 'resend' },
  ],
};

// ── HSL badge colors — import from central source, bridge underscore→hyphen keys ──

import { STATUS_HSL as CENTRAL_HSL } from '@/pages/order-details/components/StatusColors';

// badgeColorKey uses underscores, central file uses hyphens — bridge lookup
const getFilterBadgeColor = (badgeKey: string) => {
  const hyphenated = badgeKey.replace(/_/g, '-');
  return CENTRAL_HSL[hyphenated] ?? CENTRAL_HSL[badgeKey] ?? { bg: 'hsla(0,0%,50%,0.10)', text: 'hsl(0,0%,40%)' };
};

// ── Order type config ────────────────────────────────────────────────────────

const ORDER_TYPES = [
  { value: OrderTypeEnum.NORMAL, labelKey: 'order_type_normal', Icon: PackageCheck },
  { value: OrderTypeEnum.RETURN, labelKey: 'order_type_returned', Icon: CornerDownLeft },
  { value: OrderTypeEnum.EXCHANGE, labelKey: 'order_type_exchange', Icon: RefreshCw },
];

// ── Component ────────────────────────────────────────────────────────────────

interface Props {
  activeTab: OrderStatusTab;
  activeSubTab: string;
  onSubTabChange: (subTab: string) => void;
  actionRequiredCount?: number;
}

const OrdersFilterRow = ({
  activeTab,
  activeSubTab,
  onSubTabChange,
  actionRequiredCount = 0,
}: Props) => {
  const { t } = useTranslation();
  const [openDropdown, setOpenDropdown] = useState<'type' | 'status' | 'date' | 'printed' | null>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 as number | 'auto', right: 'auto' as number | 'auto' });
  // Derive from activeSubTab — no local state desync risk
  const actionRequiredOnly = activeSubTab === 'action-required';
  // Local date state — only applied when user clicks Apply
  const [localDateFrom, setLocalDateFrom] = useState('');
  const [localDateTo, setLocalDateTo] = useState('');

  const {
    setCurrentPage,
    createdAtFilter,
    orderTypeFilter,
    printedFilter,
    setCreatedAt,
    setOrderType,
    setPrinted,
    resetFilers,
  } = useFilterOrderStore();

  const hasTypeFilter = orderTypeFilter && orderTypeFilter.length > 0;
  const hasDateFilter = !!createdAtFilter;
  const hasStatusFilter = activeSubTab !== 'all';
  const hasPrintedFilter = printedFilter !== undefined;
  const hasAnyFilter = hasTypeFilter || hasDateFilter || hasStatusFilter || hasPrintedFilter;

  const statusOptions = TAB_STATUS_OPTIONS[activeTab] ?? [];

  const openDrop = (which: 'type' | 'status' | 'date' | 'printed', e: React.MouseEvent) => {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const margin = 16;
    const vw = window.innerWidth;
    const isRtl = document.documentElement.dir === 'rtl';

    // In LTR the pills are on the right → anchor dropdown's right edge to trigger's right edge
    // In RTL the pills are on the left  → anchor dropdown's left edge to trigger's left edge
    let left: number | 'auto' = 'auto';
    let right: number | 'auto' = 'auto';

    if (isRtl) {
      left = Math.max(margin, r.left);
    } else {
      right = Math.max(margin, vw - r.right);
    }

    setDropdownPos({ top: r.bottom + 6, left, right });
    // Sync local date state when opening date dropdown
    if (which === 'date') {
      setLocalDateFrom(createdAtFilter?.[0]?.format('YYYY-MM-DD') ?? '');
      setLocalDateTo(createdAtFilter?.[1]?.format('YYYY-MM-DD') ?? '');
    }
    setOpenDropdown(openDropdown === which ? null : which);
  };

  const toggleType = (val: OrderTypeEnum) => {
    const current = orderTypeFilter ?? [];
    const next = current.includes(val)
      ? current.filter((v) => v !== val)
      : [...current, val];
    setOrderType(next);
    setCurrentPage(1);
  };

  const selectStatus = (key: string) => {
    onSubTabChange(activeSubTab === key ? 'all' : key);
    setOpenDropdown(null);
  };

  const clearAll = () => {
    resetFilers();
    setCurrentPage(1);
    onSubTabChange('all');
  };

  const pillClass = (active: boolean) =>
    `inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-solid text-[12px] font-semibold transition-colors shrink-0 cursor-pointer ${
      active ? '' : 'hover:bg-gray-50'
    }`;

  return (
    <>

      {/* Action Required (Problematic tab only) */}
      {activeTab === OrderStatusTab.ON_HOLD && actionRequiredCount > 0 && (
        <button
          onClick={() => {
            onSubTabChange(actionRequiredOnly ? 'all' : 'action-required');
          }}
          className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-solid text-[12px] font-semibold transition-colors shrink-0 cursor-pointer ${
            actionRequiredOnly
              ? 'bg-red-100 border-red-400 text-red-700'
              : 'bg-red-50 border-red-200 text-red-600 hover:bg-red-100'
          }`}
        >
          <Zap className='w-3.5 h-3.5' />
          {String(t('action_required'))}
          <span
            className={`inline-flex items-center justify-center min-w-[18px] px-1 py-0.5 rounded-full text-[10px] font-bold ${
              actionRequiredOnly ? 'bg-red-600 text-white' : 'bg-red-100 text-red-600'
            }`}
          >
            {shortenNumber(actionRequiredCount)}
          </span>
        </button>
      )}

      {/* Type pill */}
      <button
        onClick={(e) => openDrop('type', e)}
        className={pillClass(!!hasTypeFilter)}
        style={{
          borderColor: hasTypeFilter ? C.primaryBorder : C.border,
          backgroundColor: hasTypeFilter ? C.primaryBg : C.white,
          color: hasTypeFilter ? C.fg : C.mutedFg,
        }}
      >
        <PackageCheck className='w-3.5 h-3.5' />
        {String(t('type'))}
        {hasTypeFilter && <span>&middot; {orderTypeFilter!.length}</span>}
        <ChevronDown className='w-3.5 h-3.5 opacity-60' />
      </button>

      {/* Status pill — only show if there are 2+ options (single option means nothing to filter) */}
      {statusOptions.length > 1 && (
        <button
          onClick={(e) => openDrop('status', e)}
          className={pillClass(hasStatusFilter)}
          style={{
            borderColor: hasStatusFilter ? C.primaryBorder : C.border,
            backgroundColor: hasStatusFilter ? C.primaryBg : C.white,
            color: hasStatusFilter ? C.fg : C.mutedFg,
          }}
        >
          <ArrowUpDown className='w-3.5 h-3.5' />
          {String(t('status'))}
          {hasStatusFilter && <span>&middot; 1</span>}
          <ChevronDown className='w-3.5 h-3.5 opacity-60' />
        </button>
      )}

      {/* Date pill */}
      <button
        onClick={(e) => openDrop('date', e)}
        className={pillClass(hasDateFilter)}
        style={{
          borderColor: hasDateFilter ? C.primaryBorder : C.border,
          backgroundColor: hasDateFilter ? C.primaryBg : C.white,
          color: hasDateFilter ? C.fg : C.mutedFg,
        }}
      >
        <CalendarDays className='w-3.5 h-3.5' />
        {String(t('date'))}
        {hasDateFilter && <span>&middot; {String(t('set'))}</span>}
        <ChevronDown className='w-3.5 h-3.5 opacity-60' />
      </button>

      {/* Printed pill */}
      <button
        onClick={(e) => openDrop('printed', e)}
        className={pillClass(hasPrintedFilter)}
        style={{
          borderColor: hasPrintedFilter ? C.primaryBorder : C.border,
          backgroundColor: hasPrintedFilter ? C.primaryBg : C.white,
          color: hasPrintedFilter ? C.fg : C.mutedFg,
        }}
      >
        <Printer className='w-3.5 h-3.5' />
        {String(t('label_status'))}
        {hasPrintedFilter && <span>&middot; 1</span>}
        <ChevronDown className='w-3.5 h-3.5 opacity-60' />
      </button>

      {/* Clear */}
      {hasAnyFilter && (
        <button
          onClick={clearAll}
          className='h-9 px-2.5 rounded-lg text-[12px] font-semibold cursor-pointer bg-transparent border-0 shrink-0 hover:bg-red-50 transition-colors'
          style={{ color: 'hsl(0, 72%, 51%)' }}
        >
          {String(t('clear_all'))}
        </button>
      )}

      {/* ── Portal dropdowns ────────────────────────────────────────────── */}
      {openDropdown &&
        createPortal(
          <>
            {/* Backdrop */}
            <div
              className='fixed inset-0'
              style={{ zIndex: 9998 }}
              onClick={() => setOpenDropdown(null)}
            />
            {/* Dropdown */}
            <div
              className='fixed rounded-2xl border border-solid p-3 min-w-[220px] overflow-hidden'
              style={{
                zIndex: 9999,
                boxSizing: 'border-box',
                top: dropdownPos.top,
                left: dropdownPos.left !== 'auto' ? dropdownPos.left : undefined,
                right: dropdownPos.right !== 'auto' ? dropdownPos.right : undefined,
                maxWidth: 'calc(100vw - 32px)',
                backgroundColor: C.white,
                borderColor: C.border,
                boxShadow:
                  '0 1px 2px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.10), 0 8px 24px rgba(0,0,0,0.06)',
              }}
            >
              {/* TYPE dropdown */}
              {openDropdown === 'type' && (
                <div className='space-y-1'>
                  <p
                    className='text-[10px] font-bold uppercase tracking-wider px-2 pb-1 m-0'
                    style={{ color: C.mutedFg }}
                  >
                    {String(t('order_type'))}
                  </p>
                  {ORDER_TYPES.map(({ value, labelKey, Icon }) => {
                    const active = orderTypeFilter?.includes(value) ?? false;
                    return (
                      <button
                        key={value}
                        onClick={() => toggleType(value)}
                        className='flex items-center gap-2.5 w-full px-2 py-2 rounded-lg text-[12px] font-semibold transition-colors bg-transparent border-0 cursor-pointer'
                        style={{
                          backgroundColor: active ? 'rgb(243,243,243)' : 'transparent',
                          color: C.fg,
                        }}
                      >
                        <span
                          className='w-4 h-4 rounded border border-solid flex items-center justify-center shrink-0'
                          style={{ borderColor: active ? C.fg : C.border }}
                        >
                          {active && <Check className='w-3 h-3' style={{ color: C.fg }} strokeWidth={3} />}
                        </span>
                        <Icon className='w-3.5 h-3.5' style={{ color: C.mutedFg }} strokeWidth={2} />
                        {String(t(labelKey))}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* STATUS dropdown */}
              {openDropdown === 'status' && (
                <div className='space-y-1'>
                  <p
                    className='text-[10px] font-bold uppercase tracking-wider px-2 pb-1 m-0'
                    style={{ color: C.mutedFg }}
                  >
                    {String(t('status'))}
                  </p>
                  {statusOptions.map(({ key, badgeColorKey, labelKey }) => {
                    const active = activeSubTab === key;
                    const hsl = getFilterBadgeColor(badgeColorKey);
                    return (
                      <button
                        key={key}
                        onClick={() => selectStatus(key)}
                        className='flex items-center gap-2.5 w-full px-2 py-2 rounded-lg text-[12px] font-semibold transition-colors bg-transparent border-0 cursor-pointer'
                        style={{
                          backgroundColor: active ? 'rgb(243,243,243)' : 'transparent',
                          color: C.fg,
                        }}
                      >
                        <span
                          className='w-2 h-2 rounded-full shrink-0'
                          style={{ background: active ? hsl.text : 'transparent' }}
                        />
                        <span
                          className='inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold'
                          style={{ background: hsl.bg, color: hsl.text }}
                        >
                          {String(t(labelKey))}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* DATE dropdown */}
              {openDropdown === 'date' && (
                <div className='space-y-3'>
                  <p
                    className='text-[10px] font-bold uppercase tracking-wider px-1 m-0'
                    style={{ color: C.mutedFg }}
                  >
                    {String(t('date_range'))}
                  </p>
                  <div className='space-y-2'>
                    <div>
                      <label className='text-[10px] block mb-1' style={{ color: C.mutedFg }}>
                        {String(t('date_from'))}
                      </label>
                      <input
                        type='date'
                        value={localDateFrom}
                        onChange={(e) => setLocalDateFrom(e.target.value)}
                        className='w-full max-w-full min-w-0 h-8 px-2 rounded-lg border border-solid text-[12px] focus:outline-none'
                        style={{ borderColor: C.border, backgroundColor: C.white, color: C.fg, boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label className='text-[10px] block mb-1' style={{ color: C.mutedFg }}>
                        {String(t('date_to'))}
                      </label>
                      <input
                        type='date'
                        value={localDateTo}
                        onChange={(e) => setLocalDateTo(e.target.value)}
                        className='w-full max-w-full min-w-0 h-8 px-2 rounded-lg border border-solid text-[12px] focus:outline-none'
                        style={{ borderColor: C.border, backgroundColor: C.white, color: C.fg, boxSizing: 'border-box' }}
                      />
                    </div>
                    <div className='flex items-center gap-2 pt-1'>
                      {(localDateFrom || localDateTo || hasDateFilter) && (
                        <button
                          onClick={() => {
                            setLocalDateFrom('');
                            setLocalDateTo('');
                            setCreatedAt(undefined);
                            setCurrentPage(1);
                          }}
                          className='text-[11px] font-semibold h-7 flex items-center bg-transparent border-0 cursor-pointer p-0 hover:underline'
                          style={{ color: 'hsl(0, 72%, 51%)' }}
                        >
                          {String(t('clear_all'))}
                        </button>
                      )}
                      <div className='flex-1' />
                      <button
                        onClick={() => {
                          if (localDateFrom || localDateTo) {
                            const from = localDateFrom ? dayjs(localDateFrom) : dayjs().subtract(1, 'month');
                            const to = localDateTo ? dayjs(localDateTo) : dayjs();
                            setCreatedAt([from, to]);
                            setCurrentPage(1);
                          }
                          setOpenDropdown(null);
                        }}
                        disabled={!localDateFrom && !localDateTo}
                        className='text-[11px] font-semibold h-7 px-4 rounded-lg border-0 cursor-pointer disabled:opacity-40 transition-colors hover:opacity-90'
                        style={{ backgroundColor: 'rgb(237, 224, 74)', color: C.fg }}
                      >
                        {String(t('apply'))}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* PRINTED dropdown */}
              {openDropdown === 'printed' && (
                <div className='space-y-1'>
                  {[
                    { value: true, labelKey: 'printed' },
                    { value: false, labelKey: 'not_printed' },
                  ].map(({ value, labelKey }) => {
                    const active = printedFilter === value;
                    return (
                      <button
                        key={labelKey}
                        onClick={() => {
                          setPrinted(active ? undefined : value);
                          setCurrentPage(1);
                          setOpenDropdown(null);
                        }}
                        className='flex items-center gap-2.5 w-full px-2 py-2 rounded-lg text-[12px] font-semibold transition-colors bg-transparent border-0 cursor-pointer'
                        style={{ backgroundColor: active ? 'rgb(243,243,243)' : 'transparent', color: C.fg }}
                      >
                        <span className='w-4 h-4 rounded border border-solid flex items-center justify-center shrink-0'
                          style={{ borderColor: active ? C.fg : C.border }}>
                          {active && <Check className='w-3 h-3' style={{ color: C.fg }} strokeWidth={3} />}
                        </span>
                        {String(t(labelKey))}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </>,
          document.body,
        )}
    </>
  );
};

export default OrdersFilterRow;
